import { useCallback, useEffect, useRef, useState } from 'react';
import { serverListApiService } from '../services/serverListApi';
import { maintenanceApiService, mergeMaintenanceChange } from '../services/maintenanceApi';
import type { ComingSoonMap, MaintenanceChange, MaintenanceMap, MaintenanceOptions } from '../services/maintenanceApi';
import { indexServerListings } from '../utils/statusMonitorTable';
import type { ServerListing } from '../utils/statusMonitorTable';

/**
 * What the rest of the network says about the servers a config describes.
 *
 * A config row is the intent — this is the outcome: whether players can reach
 * the server (the public server list) and whether somebody has taken it down
 * on purpose (Mr. Roboto's maintenance flag). Both are read the way the status
 * page reads them, on the same cadence, so a server looks the same from either
 * page. The join is by address, which is the one key every system shares.
 *
 * A window may be a coming-soon one: a new server shown before it opens. It
 * is still `true` in `maintenance`, and `comingSoon` says which windows are
 * launches, with the release time staff gave, if any.
 */

/** The public server list is identity plus liveness; a minute is its own refresh rate. */
const LISTING_REFRESH_MS = 60_000;
/** Maintenance is a button somebody presses, so it is polled on a human timescale. */
const MAINTENANCE_REFRESH_MS = 15_000;

export type ListingState = 'loading' | 'ready' | 'stale' | 'failed';

export interface FleetStatus {
  /** Lowercase address → listing. Null until the first successful read. */
  listings: Map<string, ServerListing> | null;
  listingState: ListingState;
  /** Lowercase address → in maintenance. Null until the first successful read. */
  maintenance: MaintenanceMap | null;
  /** Lowercase address → coming-soon entry. Null until the first successful read. */
  comingSoon: ComingSoonMap | null;
  refresh: () => Promise<void>;
  /** Start or end a window; the flags are re-read afterwards. */
  applyMaintenance: (addresses: string[], next: boolean, options?: MaintenanceOptions) => Promise<MaintenanceChange>;
}

export interface LiveLookup {
  listing: ServerListing | null;
  maintenance: boolean;
  /** In a coming-soon window: new, shown, not open yet. Implies `maintenance`. */
  comingSoon: boolean;
  /** The announced opening, unix seconds, or null when none was given. */
  releaseAt: number | null;
  /** False until both feeds have answered at least once. */
  loaded: boolean;
}

const key = (address: string) => address.trim().toLowerCase();

/** One server's slice of the fleet status. */
export function liveFor(fleet: FleetStatus | null | undefined, address: string): LiveLookup {
  if (!fleet) return { listing: null, maintenance: false, comingSoon: false, releaseAt: null, loaded: false };
  const soon = fleet.comingSoon?.[key(address)] ?? null;
  return {
    listing: fleet.listings?.get(key(address)) ?? null,
    maintenance: Boolean(soon) || Boolean(fleet.maintenance?.[key(address)]),
    comingSoon: Boolean(soon),
    releaseAt: soon?.releaseAt ?? null,
    loaded: fleet.listings !== null && fleet.maintenance !== null,
  };
}

export function useFleetStatus({ enabled = true }: { enabled?: boolean } = {}): FleetStatus {
  const [listings, setListings] = useState<Map<string, ServerListing> | null>(null);
  const [listingState, setListingState] = useState<ListingState>('loading');
  const [windows, setWindows] = useState<{ servers: MaintenanceMap; comingSoon: ComingSoonMap } | null>(null);
  const alive = useRef(true);

  const readListings = useCallback(async () => {
    try {
      const list = await serverListApiService.getServerList({ maxAge: LISTING_REFRESH_MS });
      if (!alive.current) return;
      setListings(indexServerListings(list));
      setListingState('ready');
    } catch {
      // The last good list stays in use; the state says it is ageing.
      if (alive.current) setListingState((previous) => (previous === 'ready' || previous === 'stale' ? 'stale' : 'failed'));
    }
  }, []);

  const readMaintenance = useCallback(async () => {
    try {
      const result = await maintenanceApiService.getMaintenance();
      if (alive.current) setWindows({ servers: result.servers, comingSoon: result.comingSoon });
    } catch {
      // A failed read keeps the last answer: clearing it would redraw a
      // planned window as an outage.
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return () => { alive.current = false; };
    void readListings();
    void readMaintenance();
    const listingTimer = setInterval(() => void readListings(), LISTING_REFRESH_MS);
    const maintenanceTimer = setInterval(() => void readMaintenance(), MAINTENANCE_REFRESH_MS);
    return () => {
      alive.current = false;
      clearInterval(listingTimer);
      clearInterval(maintenanceTimer);
    };
  }, [enabled, readListings, readMaintenance]);

  const refresh = useCallback(async () => {
    await Promise.all([readListings(), readMaintenance()]);
  }, [readListings, readMaintenance]);

  const applyMaintenance = useCallback(async (addresses: string[], next: boolean, options: MaintenanceOptions = {}) => {
    const result = await maintenanceApiService.setMaintenance(addresses, next, options);
    // Paint the change before the next poll confirms it; the poll is still
    // what makes it true for everyone else.
    setWindows((previous) => mergeMaintenanceChange(previous, result, { maintenance: next, ...options }));
    void readMaintenance();
    return result;
  }, [readMaintenance]);

  return {
    listings,
    listingState,
    maintenance: windows?.servers ?? null,
    comingSoon: windows?.comingSoon ?? null,
    refresh,
    applyMaintenance,
  };
}
